import { handleActions } from 'redux-actions';
import { LOCATION_CHANGE, replace } from 'react-router-redux';
import endpoints from '../../../config/endpoints';
import { getFormValues } from 'redux-form';

import http from '../../shared/http';
import intl from '../../shared/intl';
import checkPermissions from '../../shared/check-permissions';

import qs from 'qs';
import moment from 'moment';
import cloneDeep from 'lodash/cloneDeep';
import range from 'lodash/range';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';

import createTable from '../components/table';
import createForm from '../components/form/small';
import { default as createCalendar, getDateRangeFromCalendar } from '../../shared/calendar';
import createFilters from '../../shared/filters';

const calendar = createCalendar({
	namespace: 'newsList',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().subtract(1, 'month').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const filters = createFilters({
	namespace: 'newsList',
	stateNodeName: 'filters',
	queryBuildFunc: () => '',
	listOfFilters: [
		{
			name: 'offer_ids',
			title: 'Offers',
			placeholder: 'Add offers',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.OFFERS
			}
		},
		{
			name: 'types',
			title: 'Categories',
			placeholder: 'Add categories',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'static',
				source: 'root.lists.news-types'
			}
		}
	]
});

const table = createTable({
	namespace: 'newsList',
	stateNodeName: 'table',
	configureSort: {
		type: 'remote',
		resolverSortFn: request
	}
});

const form = createForm({
	namespace: 'newsList',
	stateNodeName: 'form'
});

// action types
const FETCH_DATA_SUCCESS = 'news-list/fetch-data-success';
const FETCH_DATA_ERROR = 'news-list/fetch-data-error';

const APPLY = 'news-list/apply';
const SHOW_FORM = 'news-list/show-form';

const GOTO_PAGE_SUCCESS = 'news-list/goto-page-success';
const TOGGLE_PROGRESS_BAR = 'news-list/toggle-progress-bar';

const DELETE_NEWS_SUCCESS = 'news-list/delete-news-success';
const APPLY_QUICK_FILTER = 'news-list/apply-quick-filter';

const SET_SEARCH_QUERY = 'news-list/set-search-query';

// permissions
export const allowedPermissions = {
	view: 'news_list',
	edit: 'news_create',
	delete: 'news_delete'
};

// initial state
const initialState = {
	data: null,
	table: table.initialState,
	calendar: calendar.initialState,
	form: form.initialState,
	filters: filters.initialState,
	pagination: {
		currentPage: 1,
		perPage: 100,
		total: null
	},
	search: '',
	quickFilters: [
		{ name: 'all', placeholder: 'All', active: true },
		{ name: 'published', placeholder: 'Published', active: false },
		{ name: 'not_published', placeholder: 'Not published', active: false }
	],
	defaultLanguage: 'en',
	showProgressBar: false,
	error: false
};

// actions
export const actions = Object.assign(
	{
		fetchData: props => (dispatch, getState) => {
			const externalQuery = (props.location && props.location.query && qs.parse(props.location.query)) || {};
			const savedFilters = filters.selectors.getSavedFilters(getState(), 'newsList');

			if (isEmpty(externalQuery) && savedFilters.entity && savedFilters.entity.filters.length) {
				return dispatch(replace(`news/?${qs.stringify(savedFilters)}`));
			}
			return request(getState, externalQuery && externalQuery.query)(dispatch).then(action => {
				if (externalQuery.entity) {
					const filtersList = externalQuery.entity.filters || [];
					dispatch(filters.actions.setFiltersFromExternalQuery(filtersList));
				}
			});
		},

		apply: () => (dispatch, getState) => {
			const searchForm = getFormValues('newsSearchForm')(getState());
			const search = searchForm && searchForm.search;

			dispatch(actions.toggleProgressBar());
			runActionsWithDelay(() => dispatch({ type: APPLY, payload: search }));
		},

		search: () => (dispatch, getState) => {
			dispatch(actions.setSearchQuery());
			dispatch(request(getState));
		},

		setSearchQuery: () => (dispatch, getState) => {
			const searchForm = getFormValues('newsSearchForm')(getState());
			const search = searchForm && searchForm.search || '';

			dispatch({
				type: SET_SEARCH_QUERY,
				payload: search
			});
		},

		clear: () => dispatch => {
			dispatch(actions.toggleProgressBar());
			runActionsWithDelay(() => dispatch(replace('/news')));
		},

		showForm: () => ({
			type: SHOW_FORM
		}),

		toggleProgressBar: () => ({
			type: TOGGLE_PROGRESS_BAR
		}),

		gotoPage: page => (dispatch, getState) => {
			dispatch(actions.toggleProgressBar());

			dispatch({
				type: GOTO_PAGE_SUCCESS,
				payload: page
			});

			request(getState)(dispatch);
		},

		deleteNews: id => (dispatch, getState) => {
			dispatch(actions.toggleProgressBar());
			http({
				url: `${endpoints.NEWS.BASE}/${id}`,
				method: 'delete',
				actions: [DELETE_NEWS_SUCCESS, FETCH_DATA_ERROR]
			})(dispatch);
		},

		applyQuickFilter: filterName => dispatch => {
			dispatch(actions.toggleProgressBar());
			runActionsWithDelay(() => dispatch({ type: APPLY_QUICK_FILTER, payload: filterName }));
		}
	},
	calendar.actions,
	table.actions,
	form.actions,
	filters.actions
);

const reducers = Object.assign(
	{
		[LOCATION_CHANGE]: (state, action) => {
			return initialState;
		},
		[FETCH_DATA_SUCCESS]: (state, action) => {
			return {
				...state,
				data: action.payload.data.rows,
				pagination: {
					...state.pagination,
					total: action.payload.data.total
				},
				showProgressBar: false,
				error: false
			};
		},
		[APPLY]: (state, action) => ({
			...state, data: null,
			search: action.payload, showProgressBar: false,
			pagination: initialState.pagination
		}),
		[GOTO_PAGE_SUCCESS]: (state, action) => ({
			...state,
			pagination: {
				...state.pagination,
				currentPage: action.payload
			}
		}),
		[DELETE_NEWS_SUCCESS]: state => ({
			...state,
			data: null,
			showProgressBar: false
		}),
		[SHOW_FORM]: state => ({
			...state,
			form: {
				...state.form,
				showForm: true
			}
		}),
		[APPLY_QUICK_FILTER]: (state, action) => ({
			...state,
			data: null,
			showProgressBar: false,
			pagination: initialState.pagination,
			quickFilters: state.quickFilters.map(filter => {
				return filter.name === action.payload ? { ...filter, active: true } : { ...filter, active: false };
			})
		}),
		[FETCH_DATA_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
		[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar }),
		[SET_SEARCH_QUERY]: (state, action) => ({ ...state, search: action.payload })
	},
	calendar.reducers,
	table.reducers,
	form.reducers,
	filters.reducers
);

// reducers
export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.newsList.data,
	getTable: table.selectors.getTable,
	getFormData: form.selectors.getFormData,
	getSearchData: state => ({ search: state.newsList.search }),
	getQuickFilters: state => state.newsList.quickFilters,
	getFilters: state => filters.selectors.getFilters(
		state => state.newsList.filters,
		state => state
	)(state),
	getCalendar: state => calendar.selectors.getCalendar(state => state.newsList.calendar, state => state)(state),
	getPagination: state => {
		const { perPage, total, currentPage } = state.newsList.pagination;
		return {
			pages: range(Math.ceil(total / perPage)),
			currentPage
		};
	},
	getFormTabLangs: state => getTabLangs(state),
	getProgressBar: state => state.newsList.showProgressBar,
	getIntl: state => intl(state),
	getCan: state => checkPermissions(state, allowedPermissions),
	getError: state => state.newsList.error
};

function request(getState, externalQuery = {}) {
	const {
		calendar, search, quickFilters,
		filters: { selectedFilters },
		pagination: { perPage, currentPage },
		table: { sortByColumn }
	} = getState().newsList;

	const activeQuickFilter = find(quickFilters, { active: true });

	const limit = perPage;
	const offset = (currentPage - 1) * perPage;

	const filters = {};

	if (selectedFilters) {
		selectedFilters.forEach(filter => {
			const values = filter.value.map(f => f.value);
			if (values.length) {
				filters[filter.name] = values;
			}
		});
	}

	const query = qs.stringify({
		offset,
		limit,
		search,
		quick_filter: activeQuickFilter && activeQuickFilter.name,
		date_range: getDateRangeFromCalendar(calendar),
		sort: [sortByColumn.index, sortByColumn.direction],
		...filters,
		...externalQuery
	});

	return http({
		url: `${endpoints.NEWS.BASE}?${query}`,
		method: 'get',
		actions: [FETCH_DATA_SUCCESS, FETCH_DATA_ERROR]
	});
}

const getTabLangs = state => {
	const { defaultLanguage, form: { currentLanguage } } = state.newsList;
	let langs = state.root.lists.languages && state.root.lists.languages.filter(lang => lang.id === 'en');

	return langs.map(lang => ({
		...lang,
		isSelected: true,
		isActive: lang.id === currentLanguage,
		isDefault: lang.id === defaultLanguage
	}));
};

/**
 * Wait when to end all action before view transit to 'uninitialized' state
 */
const runActionsWithDelay = actionFn => setTimeout(actionFn);



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/news/list/index.js